// Features an org admin can switch on or off for their organization
// (Settings → Organization). The choice is stored on the org's
// `organizationconfig` row as `enabledFeatures`, a list of the keys below.
//
// Most features are OFF until an admin enables it: a key that is missing from
// the list — including every org that has never saved one — reads as disabled.
// A feature marked `defaultOn` (one every org already had, like the
// leaderboard, news, events and wiki) is the other way round: it is ON unless the list holds
// `<key>:off`, so existing orgs keep it without anyone saving a setting.
// Adding a feature is one entry here plus a gate where it is exposed; the schema
// does not change. The leaderboard Function reads the same `leaderboard:off`
// marker (backend/leaderboard in the Selisian repo).

export const ORG_FEATURES = [
  {
    key: "featureRequests",
    labelKey: "BTN_FEATURE_REQUESTS",
    descriptionKey: "LABEL_ORG_FEATURE_FEATURE_REQUESTS_DESC",
  },
  {
    key: "feedback",
    labelKey: "BTN_FEEDBACK",
    descriptionKey: "LABEL_ORG_FEATURE_FEEDBACK_DESC",
  },
  {
    key: "leaderboard",
    labelKey: "BTN_LEADERBOARD",
    descriptionKey: "LABEL_ORG_FEATURE_LEADERBOARD_DESC",
    defaultOn: true,
  },
  {
    key: "news",
    labelKey: "BTN_NEWS",
    descriptionKey: "LABEL_ORG_FEATURE_NEWS_DESC",
    defaultOn: true,
  },
  {
    key: "events",
    labelKey: "BTN_EVENTS",
    descriptionKey: "LABEL_ORG_FEATURE_EVENTS_DESC",
    defaultOn: true,
  },
  {
    key: "wiki",
    labelKey: "BTN_WIKI",
    descriptionKey: "LABEL_ORG_FEATURE_WIKI_DESC",
    defaultOn: true,
  },
  {
    // Admin-only switch (organizationconfig edit policy). Off hides stickers
    // everywhere and removes the Sticker button; club-stickers-v1 also refuses
    // to place while `stickers:off` is stored.
    key: "stickers",
    labelKey: "LABEL_ORG_FEATURE_STICKERS",
    descriptionKey: "LABEL_ORG_FEATURE_STICKERS_DESC",
    defaultOn: true,
  },
] as const;

export type OrgFeatureKey = (typeof ORG_FEATURES)[number]["key"];

const isDefaultOn = (key: OrgFeatureKey): boolean =>
  ORG_FEATURES.some((f) => f.key === key && "defaultOn" in f && f.defaultOn);

/** The marker that switches a `defaultOn` feature off. */
export const orgFeatureOffKey = (key: OrgFeatureKey) => `${key}:off`;

/**
 * Whether `key` is on, given the org's stored list. Absent list: only the
 * `defaultOn` features are on.
 */
export const isOrgFeatureEnabled = (
  enabled: readonly string[] | null | undefined,
  key: OrgFeatureKey,
): boolean =>
  isDefaultOn(key)
    ? !enabled?.includes(orgFeatureOffKey(key))
    : !!enabled?.includes(key);

/**
 * The list to store after switching `key` on or off. Keys this build does not
 * know are kept untouched, so an older client cannot drop a feature that a newer
 * build enabled.
 */
export const withOrgFeature = (
  enabled: readonly string[] | null | undefined,
  key: OrgFeatureKey,
  on: boolean,
): string[] => {
  const off = orgFeatureOffKey(key);
  const rest = (enabled ?? []).filter((k) => k !== key && k !== off);
  if (isDefaultOn(key)) return on ? rest : [...rest, off];
  return on ? [...rest, key] : rest;
};
